/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// ============================================================================
// PRODUCT SIZE SELECTION SYNCHRONIZATION & CARD HOVER REDIRECT
// ============================================================================
(function() {

  function syncSizeSelection(productId, sizeValue, sourceElement, isInit) {
    if (!productId || !sizeValue) {
      return;
    }

    const normalizedSize = sizeValue.trim().toLowerCase();

    // 1. Sync any <variant-picker> for this product (PDP & Quick View)
    const variantPickers = document.querySelectorAll(`variant-picker[data-product-id="${productId}"]`);
    variantPickers.forEach((picker, index) => {
      if (picker === sourceElement) {
        return;
      }

      // Try finding standard radio input label matching sizeValue
      const labels = Array.from(picker.querySelectorAll('label'));
      const matchingLabel = labels.find(lbl => lbl.textContent.trim().toLowerCase() === normalizedSize);
      if (matchingLabel) {
        const inputId = matchingLabel.getAttribute('for');
        const input = picker.querySelector(`#${CSS.escape(inputId)}`);
        if (input && !input.checked) {
          input.checked = true;
          if (!isInit) {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      // Try finding dropdown combo-box button matching sizeValue
      const comboButtons = Array.from(picker.querySelectorAll('.combo-box__option-item'));
      const matchingComboBtn = comboButtons.find(btn => btn.textContent.trim().toLowerCase() === normalizedSize);
      if (matchingComboBtn) {
        if (matchingComboBtn.getAttribute('aria-selected') !== 'true') {
          if (isInit) {
            matchingComboBtn.setAttribute('aria-selected', 'true');
            const selectValueSpan = picker.querySelector('.select__selected-value');
            if (selectValueSpan) selectValueSpan.textContent = matchingComboBtn.textContent.trim();
            const hiddenInput = picker.querySelector('input[type="hidden"][data-option-position]');
            if (hiddenInput) hiddenInput.value = matchingComboBtn.getAttribute('value') || hiddenInput.value;
          } else {
            matchingComboBtn.click();
          }
        }
      }
    });

    // 2. Sync any FBT (Frequently Bought Together) drawer item dropdowns
    const fbtDrawerItems = document.querySelectorAll(`.fbt-drawer-item[data-product-id="${productId}"]`);
    fbtDrawerItems.forEach((item, index) => {
      const select = item.querySelector('.fbt-drawer-item__select');
      if (select && select !== sourceElement) {
        const option = Array.from(select.options).find(opt => {
          const optText = opt.text.trim().toLowerCase();
          return optText === normalizedSize || optText.startsWith(normalizedSize + ' ') || optText.startsWith(normalizedSize + ' -');
        });
        if (option && select.value !== option.value) {
          select.value = option.value;
          if (!isInit) {
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    });

    // 3. Sync any product card hover selectors
    if (!isInit) {
      const productCards = document.querySelectorAll(`product-item[data-product-id="${productId}"]`);
      productCards.forEach((card, index) => {
        // Sync old buttons if any exist
        const buttons = card.querySelectorAll('.product-item__size-hover-btn');
        buttons.forEach(btn => {
          const btnSize = btn.getAttribute('data-size-value');
          if (btnSize && btnSize.trim().toLowerCase() === normalizedSize) {
            btn.classList.add('is-selected');
          } else {
            btn.classList.remove('is-selected');
          }
        });

        // Sync new radio inputs
        const radios = card.querySelectorAll('.product-item__size-radio');
        radios.forEach(radio => {
          const radioSize = radio.value;
          const label = radio.closest('.product-item__size-radio-label');
          if (radioSize && radioSize.trim().toLowerCase() === normalizedSize) {
            radio.checked = true;
            if (label) label.classList.add('is-selected');
          } else {
            radio.checked = false;
            if (label) label.classList.remove('is-selected');
          }
        });
      });
    }
  }

  // Helper to extract selected size name from a variant-picker
  function getSelectedSizeName(picker) {
    // 1. Check for standard checked radio buttons (highest priority)
    const checkedInput = picker.querySelector('input[data-option-position][type="radio"]:checked');
    if (checkedInput) {
      const label = picker.querySelector(`label[for="${checkedInput.id}"]`);
      if (label) {
        return label.textContent.trim();
      }
    }

    // 2. Check for active link swatches (for options rendering as <a> links, e.g. block swatches with URLs)
    const activeLink = picker.querySelector('.block-swatch__item.is-selected, .variant-swatch__item.is-selected, .color-swatch__item.is-selected');
    if (activeLink) {
      return activeLink.textContent.trim();
    }

    // 3. Check for select dropdowns
    const select = picker.querySelector('select[data-option-position]');
    if (select && select.selectedIndex >= 0) {
      return select.options[select.selectedIndex].text.trim();
    }

    // 4. Check for combo-box selected value indicator span
    const selectValueSpan = picker.querySelector('.select__selected-value');
    if (selectValueSpan) {
      return selectValueSpan.textContent.trim();
    }

    // 5. Fallback: Check the server-rendered selected option value display span for Size
    const optionInfos = Array.from(picker.querySelectorAll('.product-form__option-info'));
    const sizeInfo = optionInfos.find(info => {
      const name = info.querySelector('.product-form__option-name');
      return name && name.textContent.toLowerCase().includes('size');
    });
    if (sizeInfo) {
      const valSpan = sizeInfo.querySelector('.product-form__option-value');
      if (valSpan) {
        return valSpan.textContent.trim();
      }
    }

    return null;
  }

  // Listen to variantpicker option changes
  document.addEventListener('change', function(event) {
    const picker = event.target.closest('variant-picker');
    if (picker) {
      const productId = picker.getAttribute('data-product-id');
      const sizeValue = getSelectedSizeName(picker);
      if (productId && sizeValue) {
        syncSizeSelection(productId, sizeValue, picker);
      }
    }
  });

  // Listen to theme's variant:changed custom event (handles PDP / Quick View loads & ajax combinations)
  document.addEventListener('variant:changed', function(event) {
    const variant = event.detail.variant;
    const form = event.target;
    if (!variant || !form) return;
    
    const picker = form.closest('variant-picker') || document.querySelector(`variant-picker[form-id="${form.id}"]`);
    if (picker) {
      const productId = picker.getAttribute('data-product-id');
      const sizeValue = getSelectedSizeName(picker);
      if (productId && sizeValue) {
        syncSizeSelection(productId, sizeValue, picker);
      }
    }
  });

  // Listen to FBT select changes
  document.addEventListener('change', function(event) {
    const select = event.target.closest('.fbt-drawer-item__select');
    if (select) {
      const drawerItem = select.closest('.fbt-drawer-item');
      const productId = drawerItem?.getAttribute('data-product-id');
      const selectedOptionText = select.options[select.selectedIndex].text;
      const sizeValue = selectedOptionText.split(' - ')[0].split(' (')[0].trim();
      syncSizeSelection(productId, sizeValue, select);
    }
  });

  // Listen to product card hover sizes click to select and redirect (old button list)
  document.addEventListener('click', function(event) {
    const btn = event.target.closest('.product-item__size-hover-btn');
    if (btn) {
      event.preventDefault();
      event.stopPropagation();
      const productId = btn.getAttribute('data-product-id');
      const sizeValue = btn.getAttribute('data-size-value');
      const variantUrl = btn.getAttribute('data-variant-url');
      
      // Update UI selection locally
      syncSizeSelection(productId, sizeValue, btn);

      // Perform redirect to product page with selected variant preselected
      if (variantUrl) {
        window.location.href = variantUrl;
      } else {
        console.warn("Antigravity: No variant URL found on button!");
      }
    }
  });

  // Listen to product card hover sizes radio change to select and redirect (new radio list)
  document.addEventListener('change', function(event) {
    const radio = event.target.closest('.product-item__size-radio');
    if (radio && radio.checked) {
      const productId = radio.getAttribute('data-product-id');
      const sizeValue = radio.value;
      const variantUrl = radio.getAttribute('data-variant-url');
      
      // Update UI selection locally
      syncSizeSelection(productId, sizeValue, radio);

      // Perform redirect to product page with selected variant preselected
      if (variantUrl) {
        window.location.href = variantUrl;
      } else {
        console.warn("Antigravity: No variant URL found on radio!");
      }
    }
  });

  function isPageReload() {
    try {
      const navEntries = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (navEntries && navEntries.length > 0) {
        return navEntries[0].type === 'reload';
      }
      if (window.performance && window.performance.navigation) {
        return window.performance.navigation.type === 1;
      }
    } catch (e) {}
    return false;
  }

  function cleanVariantUrlParam() {
    if (window.location.search && window.location.search.includes('variant=')) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        const newSearch = url.searchParams.toString();
        const newUrl = url.pathname + (newSearch ? '?' + newSearch : '') + url.hash;
        window.history.replaceState({}, document.title, newUrl);
      } catch (e) {}
    }
  }

  function applyVariantFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (!variantId) return false;

    // Find radio swatch for this variant
    const swatchRadio = document.querySelector(
      `.block-swatch__radio[data-option-variant-id="${variantId}"], .block-swatch__radio[value="${variantId}"]`
    );
    if (swatchRadio) {
      swatchRadio.checked = true;
      const selector = swatchRadio.closest('.product-form__option-selector');
      if (selector) {
        const label = selector.querySelector(`label[for="${CSS.escape(swatchRadio.id)}"]`);
        const valSpan = selector.querySelector('.product-form__option-value');
        if (valSpan && label) {
          valSpan.textContent = label.textContent.trim();
        }
        selector.classList.remove('has-size-error');
        const errorMsg = selector.querySelector('.size-selection-error');
        if (errorMsg) errorMsg.remove();
      }
      swatchRadio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    // Link swatches
    const activeLink = document.querySelector(`.block-swatch a.block-swatch__item[href*="variant=${variantId}"]`);
    if (activeLink) {
      activeLink.classList.add('is-selected');
      const valSpan = activeLink.closest('.product-form__option-selector')?.querySelector('.product-form__option-value');
      if (valSpan) {
        valSpan.textContent = activeLink.textContent.trim();
      }
      return true;
    }

    return false;
  }

  function resetProductCardSelections() {
    document.querySelectorAll('.product-item__size-radio').forEach(function(radio) {
      radio.checked = false;
      const label = radio.closest('.product-item__size-radio-label');
      if (label) {
        label.classList.remove('is-selected');
      }
    });
    document.querySelectorAll('.product-item__size-hover-btn').forEach(function(btn) {
      btn.classList.remove('is-selected');
    });
  }

  function unselectPDPSizeSelections() {
    const sizeSelectors = document.querySelectorAll(
      '.product-form__option-selector--size, .product-form__option-selector'
    );

    sizeSelectors.forEach(function(selector) {
      const nameEl = selector.querySelector('.product-form__option-name');
      const isSize = selector.classList.contains('product-form__option-selector--size') ||
                     (nameEl && /size|waist|chest|length/i.test(nameEl.textContent));

      if (isSize) {
        selector.querySelectorAll('input[type="radio"]').forEach(function(radio) {
          radio.checked = false;
        });

        selector.querySelectorAll('.block-swatch__item, .variant-swatch__item, .is-selected').forEach(function(el) {
          el.classList.remove('is-selected');
        });

        const valSpan = selector.querySelector('.product-form__option-value');
        if (valSpan) {
          valSpan.textContent = '';
        }

        const selectValueSpan = selector.querySelector('.select__selected-value');
        if (selectValueSpan) {
          selectValueSpan.textContent = 'Select Size';
        }
        selector.querySelectorAll('.combo-box__option-item').forEach(function(btn) {
          btn.setAttribute('aria-selected', 'false');
        });
      }
    });

    if (typeof updateSizeChartButtons === 'function') {
      updateSizeChartButtons();
    }
  }

  // Update selected size text and clear error on user swatch click
  document.addEventListener('change', function(event) {
    const radio = event.target.closest('.block-swatch__radio');
    if (radio && radio.checked) {
      const selector = radio.closest('.product-form__option-selector');
      if (selector) {
        const label = selector.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
        const valSpan = selector.querySelector('.product-form__option-value');
        if (valSpan && label) {
          valSpan.textContent = label.textContent.trim();
        }
        selector.classList.remove('has-size-error');
        const errorMsg = selector.querySelector('.size-selection-error');
        if (errorMsg) errorMsg.remove();
      }
    }
  });

  // Validate size selection when Add To Cart or Buy Now is clicked
  function validateSizeBeforeAction(event) {
    const trigger = event.target.closest('#AddToCart, #StickyAddToCart, [data-product-add-to-cart-button], #button-buynow, .button--buy-now, #gokwik-buy-now, .gokwik-checkout button');
    if (!trigger) return;

    const sizeSelector = document.querySelector('.product-form__option-selector--size') ||
      Array.from(document.querySelectorAll('.product-form__option-selector')).find(function(sel) {
        const nameEl = sel.querySelector('.product-form__option-name');
        return nameEl && /size|waist|chest|length/i.test(nameEl.textContent);
      });

    if (!sizeSelector) return;

    const checkedRadio = sizeSelector.querySelector('input[type="radio"]:checked');
    const activeLink = sizeSelector.querySelector('.block-swatch__item.is-selected');
    const comboSelected = sizeSelector.querySelector('.combo-box__option-item[aria-selected="true"]');

    if (!checkedRadio && !activeLink && !comboSelected) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const isBuyNow = trigger.id === 'button-buynow' || trigger.classList.contains('button--buy-now') || trigger.id === 'gokwik-buy-now' || Boolean(trigger.closest('product-buy-now'));
      window.pdpPendingAction = isBuyNow ? 'buynow' : 'addtocart';
      window.pdpPendingBtn = trigger;
      window.lastClickedBtn = trigger;

      if (typeof window.showVariantPopup === 'function') {
        window.showVariantPopup();
        return false;
      }
      const popup = document.getElementById('variant-popup');
      if (popup) {
        popup.style.display = 'flex';
        const doneBtn = document.getElementById('variant-popup-done');
        const checkedPopupRadio = popup.querySelector('input[type="radio"]:checked');
        if (doneBtn) {
          doneBtn.disabled = !checkedPopupRadio;
          doneBtn.classList.toggle('enabled', Boolean(checkedPopupRadio));
        }
        return false;
      }

      sizeSelector.classList.add('has-size-error');
      let errorMsg = sizeSelector.querySelector('.size-selection-error');
      if (!errorMsg) {
        errorMsg = document.createElement('div');
        errorMsg.className = 'size-selection-error';
        errorMsg.style.cssText = 'color: #d9534f; font-size: 13px; font-weight: 600; margin-top: 8px;';
        errorMsg.textContent = 'Please select a size before proceeding.';
        sizeSelector.appendChild(errorMsg);
      }

      sizeSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
  }

  document.addEventListener('click', validateSizeBeforeAction, true);
  document.addEventListener('submit', function(event) {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (form) {
      const sizeSelector = document.querySelector('.product-form__option-selector--size') ||
        Array.from(document.querySelectorAll('.product-form__option-selector')).find(function(sel) {
          const nameEl = sel.querySelector('.product-form__option-name');
          return nameEl && /size|waist|chest|length/i.test(nameEl.textContent);
        });
      if (sizeSelector) {
        const checkedRadio = sizeSelector.querySelector('input[type="radio"]:checked');
        const activeLink = sizeSelector.querySelector('.block-swatch__item.is-selected');
        const comboSelected = sizeSelector.querySelector('.combo-box__option-item[aria-selected="true"]');
        if (!checkedRadio && !activeLink && !comboSelected) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          const popup = document.getElementById('variant-popup');
          if (popup) {
            window.pdpPendingAction = 'addtocart';
            window.pdpPendingBtn = form.querySelector('#AddToCart, [type="submit"]');
            window.lastClickedBtn = window.pdpPendingBtn;
            if (typeof window.showVariantPopup === 'function') {
              window.showVariantPopup();
            } else {
              popup.style.display = 'flex';
            }
          } else {
            sizeSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }
  }, true);

  // Run initial synchronization on page load
  function initSyncOnLoad() {
    resetProductCardSelections();

    const isReload = isPageReload();
    const hasVariantParam = window.location.search && window.location.search.includes('variant=');

    if (isReload) {
      cleanVariantUrlParam();
      unselectPDPSizeSelections();
    } else if (hasVariantParam) {
      applyVariantFromUrl();
    } else {
      unselectPDPSizeSelections();
    }
  }

  function scheduleInitSync() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(initSyncOnLoad, { timeout: 2000 });
    } else {
      window.requestAnimationFrame(initSyncOnLoad);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitSync);
  } else {
    scheduleInitSync();
  }

  window.addEventListener('pageshow', function(event) {
    if (event.persisted || isPageReload()) {
      cleanVariantUrlParam();
      resetProductCardSelections();
      unselectPDPSizeSelections();
    }
  });
})();

// ============================================================================
// DYNAMIC SIZE SWATCH SORTING (ASCENDING NUMERIC & STANDARD APPAREL SIZES)
// ============================================================================
(function() {
  const SIZE_ORDER_MAP = {
    'xxxs': 1, '3xs': 1,
    'xxs': 2, '2xs': 2,
    'xs': 3,
    's': 4, 'small': 4,
    'm': 5, 'med': 5, 'medium': 5,
    'l': 6, 'large': 6,
    'xl': 7, '1xl': 7, 'x-large': 7,
    'xxl': 8, '2xl': 8, 'xx-large': 8,
    'xxxl': 9, '3xl': 9, 'xxx-large': 9,
    'xxxxl': 10, '4xl': 10,
    'xxxxxl': 11, '5xl': 11,
    '6xl': 12,
    '7xl': 13,
    '8xl': 14,
    'os': 100, 'onesize': 100, 'one-size': 100, 'freesize': 100, 'free-size': 100
  };

  function getElementText(el) {
    if (!el) return '';
    if (el.getAttribute('data-size-value')) {
      return el.getAttribute('data-size-value').trim();
    }
    const radio = el.querySelector('.product-item__size-radio');
    if (radio && radio.value) {
      return radio.value.trim();
    }
    const target = el.querySelector('.block-swatch__item, .variant-swatch__item, .combo-box__option-item, span, label, button') || el;
    const badge = target.querySelector ? target.querySelector('.block-swatch__inventory-badge') : null;
    if (badge) {
      return target.textContent.replace(badge.textContent, '').trim();
    }
    return (target.textContent || '').trim();
  }

  function compareSizeValues(textA, textB) {
    const cleanA = textA.toLowerCase().replace(/[^a-z0-9.]/g, '');
    const cleanB = textB.toLowerCase().replace(/[^a-z0-9.]/g, '');

    const rankA = SIZE_ORDER_MAP[cleanA];
    const rankB = SIZE_ORDER_MAP[cleanB];

    // Case 1: Both are recognized standard clothing sizes (e.g. XS, S, M, L, XL, XXL, XXXL)
    if (rankA !== undefined && rankB !== undefined) {
      return rankA - rankB;
    }

    // Case 2: Numeric sizes (e.g. 30, 32, 34, 36, 38, or 32.5 or 38S/38R/38L)
    const matchA = textA.match(/^[\s]*([0-9]+(?:\.[0-9]+)?)/);
    const matchB = textB.match(/^[\s]*([0-9]+(?:\.[0-9]+)?)/);

    if (matchA && matchB) {
      const numA = parseFloat(matchA[1]);
      const numB = parseFloat(matchB[1]);
      if (numA !== numB) {
        return numA - numB;
      }
      return textA.localeCompare(textB, undefined, { numeric: true, sensitivity: 'base' });
    }

    // Case 3: If one is standard size and other is not numeric
    if (rankA !== undefined && !matchB) return -1;
    if (rankB !== undefined && !matchA) return 1;

    // Case 4: Natural alphabetical fallback
    return textA.localeCompare(textB, undefined, { numeric: true, sensitivity: 'base' });
  }

  function sortSwatchList(list) {
    if (!list) return;

    const swatches = Array.from(list.children).filter(function(child) {
      return child.classList.contains('block-swatch') || 
             child.classList.contains('product-item__size-hover-item') ||
             child.classList.contains('product-item__size-radio-label') ||
             child.classList.contains('product-item__size-hover-btn') ||
             child.classList.contains('variant-swatch') ||
             child.classList.contains('variant-button') ||
             child.classList.contains('combo-box__option-item');
    });

    if (swatches.length <= 1) return;

    const items = swatches.map(function(swatch) {
      return {
        el: swatch,
        text: getElementText(swatch)
      };
    });

    // Verify if re-sorting is necessary to avoid redundant DOM operations
    let isSorted = true;
    for (let i = 0; i < items.length - 1; i++) {
      if (compareSizeValues(items[i].text, items[i + 1].text) > 0) {
        isSorted = false;
        break;
      }
    }

    if (isSorted) return;

    items.sort(function(a, b) {
      return compareSizeValues(a.text, b.text);
    });

    items.forEach(function(item) {
      list.appendChild(item.el);
    });
  }

  function sortAllSizeSwatches(container) {
    const root = container || document;

    // 1. Product card hover size lists
    root.querySelectorAll('.product-item__sizes-hover-list').forEach(sortSwatchList);

    // 2. Product page PDP & Quick View variant option lists (specifically size selectors)
    root.querySelectorAll('.product-form__option-selector--size .block-swatch-list, .product-form__option-selector--size .combo-box__option-list, .product-form__option-selector--size .variant-swatch-list').forEach(sortSwatchList);

    // 3. Option selectors where name contains size/waist/chest/length
    root.querySelectorAll('.product-form__option-selector').forEach(function(selector) {
      const optName = selector.querySelector('.product-form__option-name');
      if (optName) {
        const nameText = optName.textContent.toLowerCase();
        if (nameText.includes('size') || nameText.includes('waist') || nameText.includes('chest') || nameText.includes('length')) {
          const swatchList = selector.querySelector('.block-swatch-list, .combo-box__option-list, .variant-swatch-list');
          if (swatchList) {
            sortSwatchList(swatchList);
          }
        }
      }
    });

    // 4. Quick variant popups
    root.querySelectorAll('.variant-picker-popup .variant-options, .product-card-variant-picker .variant-options, .popup-swatch').forEach(sortSwatchList);
  }

  // Initial sort
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      sortAllSizeSwatches();
    });
  } else {
    sortAllSizeSwatches();
  }

  window.addEventListener('pageshow', function() {
    sortAllSizeSwatches();
  });

  // Re-sort on variant changes or Shopify section dynamic reloads
  document.addEventListener('variant:changed', function() {
    sortAllSizeSwatches();
  });
  document.addEventListener('shopify:section:load', function(event) {
    sortAllSizeSwatches(event.target);
  });

  // Observe dynamically injected DOM elements (quick view modals, facet filters, infinite scroll)
  let sortDebounceTimeout = null;
  const observer = new MutationObserver(function(mutations) {
    let hasRelevantNodes = false;
    for (let i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length > 0) {
        hasRelevantNodes = true;
        break;
      }
    }
    if (hasRelevantNodes) {
      clearTimeout(sortDebounceTimeout);
      sortDebounceTimeout = setTimeout(function() {
        sortAllSizeSwatches();
      }, 30);
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();